<template>
  <div class="mention-input relative flex-1 min-w-0">
    <div
      v-if="suggestion.open"
      class="absolute bottom-full left-0 z-10 mb-2 max-h-[280px] w-full min-w-[min(300px,calc(100vw-2rem))] overflow-y-auto rounded-xl border border-[#E9E6F3] bg-white p-1 shadow-[0_8px_24px_rgba(58,61,68,0.12)] ui-scrollbar"
    >
      <button
        v-for="(item, index) in suggestion.items"
        :key="item.id"
        type="button"
        class="block w-full rounded-lg px-3 py-2 text-left transition-colors"
        :class="index === suggestion.index ? 'bg-[#E8EFFF]' : 'hover:bg-[#F4F6FA]'"
        @mousedown.prevent="selectItem(index)"
        @mouseenter="suggestion.index = index"
      >
        <ChatPublicationSummary :item="item" />
      </button>
      <p v-if="!suggestion.items.length" class="px-3 py-2 text-sm text-[#99ABBA]">
        Публикаций не найдено
      </p>
    </div>
    <EditorContent
      :editor="editor"
      class="mention-input__field"
      :class="{ 'mention-input__field--disabled': disabled }"
    />
  </div>
</template>

<script setup lang="ts">
  import { EditorContent, useEditor } from "@tiptap/vue-3";
  import Document from "@tiptap/extension-document";
  import Paragraph from "@tiptap/extension-paragraph";
  import Text from "@tiptap/extension-text";
  import History from "@tiptap/extension-history";
  import Placeholder from "@tiptap/extension-placeholder";
  import Mention from "@tiptap/extension-mention";
  import type { PublicationMention } from "~/common/chatChannels";

  const props = defineProps<{
    placeholder?: string;
    disabled?: boolean;
    mentions?: PublicationMention[];
    // Enter отправляет, Shift+Enter переносит строку. Иначе отправка — Ctrl/Cmd+Enter
    submitOnEnter?: boolean;
  }>();

  const emit = defineEmits<{
    (e: "input", value: string): void;
    (e: "submit"): void;
  }>();

  const model = defineModel<string>({ required: true });

  const suggestion = reactive({
    open: false,
    items: [] as PublicationMention[],
    index: 0,
    command: null as null | ((item: { id: string; label: string }) => void),
  });

  const filterMentions = (query: string) => {
    const q = query.trim().toLowerCase();
    return (props.mentions ?? []).filter(
      (item) => !q || String(item.id).includes(q) || item.label.toLowerCase().includes(q),
    );
  };

  const selectItem = (index: number) => {
    const item = suggestion.items[index];
    if (item) suggestion.command?.({ id: String(item.id), label: item.label });
  };

  const syncSuggestion = (props: any) => {
    suggestion.items = props.items;
    suggestion.index = 0;
    suggestion.command = props.command;
  };

  const getText = () => editor.value?.getText({ blockSeparator: "\n" }) ?? "";

  const editor = useEditor({
    editable: !props.disabled,
    extensions: [
      Document,
      Paragraph,
      Text,
      History,
      Placeholder.configure({ placeholder: () => props.placeholder ?? "" }),
      Mention.configure({
        HTMLAttributes: { class: "mention-input__mention" },
        renderText: ({ node }) => `@${node.attrs.id}`,
        renderHTML: ({ options, node }) => ["span", options.HTMLAttributes, `@${node.attrs.id}`],
        suggestion: {
          char: "@",
          items: ({ query }) => filterMentions(query),
          render: () => ({
            onStart: (props) => {
              syncSuggestion(props);
              suggestion.open = true;
            },
            onUpdate: syncSuggestion,
            onKeyDown: ({ event }) => {
              if (event.key === "Escape") {
                suggestion.open = false;
                return true;
              }
              if (!suggestion.open || !suggestion.items.length) return false;
              const count = suggestion.items.length;
              if (event.key === "ArrowDown") {
                suggestion.index = (suggestion.index + 1) % count;
                return true;
              }
              if (event.key === "ArrowUp") {
                suggestion.index = (suggestion.index - 1 + count) % count;
                return true;
              }
              if (event.key === "Enter" || event.key === "Tab") {
                selectItem(suggestion.index);
                return true;
              }
              return false;
            },
            onExit: () => {
              suggestion.open = false;
              suggestion.command = null;
            },
          }),
        },
      }),
    ],
    editorProps: {
      handleKeyDown: (_view, event) => {
        // Пока открыт список публикаций, Enter выбирает пункт
        if (event.key !== "Enter" || suggestion.open) return false;
        if (event.ctrlKey || event.metaKey || (props.submitOnEnter && !event.shiftKey)) {
          emit("submit");
          return true;
        }
        if (props.submitOnEnter) {
          editor.value?.commands.splitBlock();
          return true;
        }
        return false;
      },
    },
    onUpdate: () => {
      const text = getText();
      model.value = text;
      emit("input", text);
    },
  });

  const escapeHtml = (value: string) =>
    value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  // Снаружи поле только очищают после отправки
  watch(model, (value) => {
    if (!editor.value || value === getText()) return;
    editor.value.commands.setContent(
      value ? value.split("\n").map((line) => `<p>${escapeHtml(line)}</p>`).join("") : "",
    );
  });

  watch(
    () => props.disabled,
    (disabled) => editor.value?.setEditable(!disabled),
  );

  const focus = () => editor.value?.commands.focus("end");

  onBeforeUnmount(() => editor.value?.destroy());

  defineExpose({ focus });
</script>

<style scoped lang="scss">
  .mention-input__field {
    :deep(.ProseMirror) {
      @apply min-h-[52px] max-h-[100px] overflow-y-auto rounded-xl border border-[#E9E6F3] bg-white p-4 text-sm leading-[1.4] text-[#3A3D44] outline-none transition-colors whitespace-pre-wrap break-words;

      &:focus {
        @apply border-primary;
      }

      p {
        @apply m-0;
      }

      p.is-editor-empty:first-child::before {
        content: attr(data-placeholder);
        @apply pointer-events-none float-left h-0 text-[#99ABBA];
      }
    }

    :deep(.mention-input__mention) {
      @apply rounded-md bg-primary/10 px-1 font-medium text-primary;
    }

    &--disabled :deep(.ProseMirror) {
      @apply cursor-not-allowed bg-[#F5F7FA];
    }
  }
</style>
